import type { ReactNode } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ContextFileStatusChip } from './ContextFileStatusChip';
import { type OrgContextFile } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  includeBtn: {
    minWidth: 90,
  },
  excludeBtn: {
    minWidth: 90,
    color: theme.palette.text.secondary,
  },
  emptyRow: {
    color: theme.palette.text.secondary,
  },
}));

export interface OrgContextSetSelectorProps {
  files: OrgContextFile[];
  readOnly: boolean;
  onToggle: (fileId: string, included: boolean) => void;
  loadingFileIds: string[];
}

export function OrgContextSetSelector({
  files,
  readOnly,
  onToggle,
  loadingFileIds,
}: OrgContextSetSelectorProps) {
  const classes = useStyles();
  const { t } = useTranslation('projects');

  if (files.length === 0) {
    return (
      <Box py={2}>
        <Typography variant="body2" className={classes.emptyRow}>
          {t('contextFiles.emptyOrgFiles', {
            defaultValue: 'No org-level files available.',
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t('table.name')}</TableCell>
          <TableCell>{t('table.typeTag')}</TableCell>
          <TableCell>{t('table.status')}</TableCell>
          {!readOnly && (
            <TableCell align="right">
              {t('table.actions', { defaultValue: 'Actions' })}
            </TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {files.map(file => {
          const isLoading = loadingFileIds.includes(file.id);

          let action: ReactNode = null;
          if (isLoading) {
            action = <CircularProgress size={20} />;
          } else if (file.isIncluded) {
            action = (
              <Button
                size="small"
                variant="outlined"
                className={classes.excludeBtn}
                onClick={() => onToggle(file.id, false)}
              >
                {t('contextFiles.excludeFile')}
              </Button>
            );
          } else {
            action = (
              <Button
                size="small"
                variant="contained"
                color="primary"
                className={classes.includeBtn}
                onClick={() => onToggle(file.id, true)}
              >
                {t('contextFiles.includeFile')}
              </Button>
            );
          }

          return (
            <TableRow key={file.id}>
              <TableCell>{file.name}</TableCell>
              <TableCell>{file.fileTypeTag}</TableCell>
              <TableCell>
                <ContextFileStatusChip status={file.status} />
              </TableCell>
              {!readOnly && <TableCell align="right">{action}</TableCell>}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
